import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useConfiguration } from '../../context/configurationContext';
import {
  NamedLink,
  Button,
  IconSpinner,
  AspectRatioWrapper,
  ResponsiveImage,
} from '../../components';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { ensureListing } from '../../util/data';
import {
  calculateCartSubtotal,
  capitalizeFirstLetter,
  filterAvailableBookingSlots,
  formatDateRange,
} from '../../util/genericHelpers';
import {
  addItemToCart,
  currentCartItemIdSelector,
  addToCartInProgressSelector,
  addToCartErrorSelector,
  deleteItemFromCart,
} from './CartPage.duck';
import { removeItemFromCart } from '../../util/cartHelpers';
import { selectIsAuthenticated } from '../../ducks/auth.duck';
import { ADD, ADD_ICON, SUBTRACT, SUBTRACT_ICON } from '../../util/constants';
import css from './CartPage.module.css';
import { createSlug } from '../../util/urlHelpers';
import { richText } from '../../util/richText';
import { formatMoney } from '../../util/currency';
import { getBookingDataArr, getUserCartDetails } from '../../util/dataExtractor';
import Select from 'react-select';
import { customSelectStyles } from '../../styles/genericStyles';
import Icons from '../../components/Icons/Icons';

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, { loadAfterInitialRendering: 3000 });

const CartPanel = ({
  className,
  rootClassName,
  listing,
  renderSizes,
  currentUser,
  setStockDetails,
  currentCartItem,
  stockDetails,
  marketplaceCurrency,
  setBookingSlot,
  bookingSlot,
}) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const config = useConfiguration();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const addToCartInProgress = useSelector(addToCartInProgressSelector);
  const addToCartError = useSelector(addToCartErrorSelector);
  const currentCartItemId = useSelector(currentCartItemIdSelector);
  const cartItems = getUserCartDetails(currentUser);
  const bookingData = getBookingDataArr(listing);
  const availableSlots = filterAvailableBookingSlots(bookingData);
  const [currentItem, setCurrentItem] = useState(
    currentCartItem?.item || { stockCount: 1, bookingSlot: null }
  );

  const [stockCount, setStockCount] = useState(currentItem.stockCount);
  const [selectedSlotValue, setSelectedSlotValue] = useState('');
  const [isHovered, setHovered] = useState(false);
  const MIN_LENGTH_FOR_LONG_WORDS = 10;
  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const { title = '', price, publicData } = listing?.attributes || {};
  const firstImage = listing.images?.[0] || null;
  const variants = firstImage
    ? Object.keys(firstImage.attributes.variants).filter(k => k.startsWith(variantPrefix))
    : [];
  const slug = createSlug(title);
  const currentListing = ensureListing(listing);
  const id = currentListing?.id?.uuid;
  const seatsCount = Number(bookingSlot?.seats) || 1;
  const availableQuantity = seatsCount;
  const currentStockDetails = stockDetails.filter(sd => sd.listingId === id);

  useEffect(() => {
    const newItem = currentCartItem?.item || { stockCount: 1, bookingSlot: null };
    setCurrentItem(newItem);
    setStockCount(newItem.stockCount);

    if (newItem.bookingSlot) {
      setBookingSlot(newItem.bookingSlot);
      setSelectedSlotValue(JSON.stringify(newItem.bookingSlot));
    } else {
      setBookingSlot(null);
      setSelectedSlotValue('');
    }
  }, [currentCartItem?.item?.stockCount, currentCartItem?.item, setBookingSlot]);

  const priceData = (price, intl) => {
    if (price && price.currency === config.currency) {
      const formattedPrice = formatMoney(intl, price);
      return { formattedPrice, priceTitle: formattedPrice };
    }
    return {
      formattedPrice: intl.formatMessage(
        { id: 'ListingCard.unsupportedPrice' },
        { currency: price.currency }
      ),
      priceTitle: intl.formatMessage(
        { id: 'ListingCard.unsupportedPriceTitle' },
        { currency: price.currency }
      ),
    };
  };

  const { priceTitle } = priceData(price, intl);

  const itemTotal = calculateCartSubtotal(currentStockDetails, intl, marketplaceCurrency);

  const onToggleQuantity = ({
    type,
    currentItem,
    currentCartItem,
    setCurrentItem,
    setStockDetails,
    stockDetails,
  }) => {
    const step = 1;
    const change = type === ADD ? step : -step;
    const newStockCount = stockCount + change;
    if (newStockCount < 1 || newStockCount > seatsCount) return;

    setStockCount(newStockCount);
    const updatedItem = { ...currentItem, stockCount: newStockCount };
    setCurrentItem(updatedItem);

    const updatedStockDetails = stockDetails.map(item =>
      item.listingId === id ? updatedItem : item
    );
    setStockDetails(updatedStockDetails);

    if (isAuthenticated) {
      dispatch(addItemToCart({ id, cartItems: updatedStockDetails.map(item => item.listingId) }));
    }
  };

  const handleRemoveItem = () => {
    dispatch(
      deleteItemFromCart({
        id,                       
        isAuthenticated,
        cartItems,             
      })
    );
  };

  return (
    <div className={classNames(rootClassName || css.root, className)}>
      {addToCartError && (
        <div className={css.error}>
          <FormattedMessage id="CartPage.addToCartError" />
        </div>
      )}
      <div className={css.productDetails}>
        <div className={css.productDetailsLeft}>
          <div className={css.productImg}>
            <NamedLink name="ListingPage" params={{ id, slug }}>
              <AspectRatioWrapper
                className={css.aspectRatioWrapper}
                width={aspectWidth}
                height={aspectHeight}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
              >
                <LazyImage
                  rootClassName={css.rootForImage}
                  alt={title}
                  image={firstImage}
                  variants={variants}
                  sizes={renderSizes}
                />
              </AspectRatioWrapper>
            </NamedLink>
          </div>
          <div className={css.info}>
            <div className={css.mainInfo}>
              <div className={css.title}>
                <NamedLink name="ListingPage" params={{ id, slug }}>
                  {richText(title, {
                    longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
                    longWordClass: css.longWord,
                  })}
                </NamedLink>
              </div>
              <div className={css.authorInfo}>Balera</div>
              <div className={css.authorInfo}>Size: Adult-Small</div>
              {publicData?.brand && (
                <div className={css.authorInfo}>{capitalizeFirstLetter(publicData.brand)}</div>
              )}
            </div>
            <div className={css.removeListing} onClick={handleRemoveItem}>
              {currentCartItemId === id ? (
                <IconSpinner />
              ) : (
                <>
                  <Icons name="trash" /> <FormattedMessage id="CartPage.removeItem" />
                </>
              )}
            </div>
          </div>
        </div>
        <div className={css.productDetailsRight}>
          <div className={css.stock}>
            {/* <Button
              type="button"
              onClick={() =>
                onToggleQuantity({
                  type: SUBTRACT,
                  currentItem,
                  currentCartItem,
                  setCurrentItem,
                  setStockDetails,
                  stockDetails,
                })
              }
              disabled={stockCount <= 1 || addToCartInProgress}
            >
              <Icons name="minus" />
            </Button> */}
            <span className={css.stockCount}>{stockCount || 1}</span>
            {/* <Button
              type="button"
              onClick={() =>
                onToggleQuantity({
                  type: ADD,
                  currentItem,
                  currentCartItem,
                  setCurrentItem,
                  setStockDetails,
                  stockDetails,
                })
              }
              disabled={stockCount >= seatsCount || addToCartInProgress}
            >
              <Icons name="plus" />
            </Button> */}
          </div>
          <div className={css.priceSec}>
            <div className={css.price}>
              <div className={css.priceValue} title={priceTitle}>
                {itemTotal}
              </div>
              {config.listing.showUnitTypeTranslations && (
                <div className={css.perUnit}>
                  <FormattedMessage id="CartPage.perUnit" />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartPanel;
